import { useEffect, useRef } from "react";
import { domEventService } from "@/common/services/domEventService";
import { CHAT_BOT_REQUEST, CHAT_RESPONSE, START_LIVECHAT_REQUESTED } from "@/common/events/chatWidgetEvents";
import { useChatContext } from "@/common/context/ChatContext";
import type { 
  ChatMessage,
  ChatBotRequestPayload,
  ChatResponsePayload,
  SystemMessage
} from "@/common/types/chat.types";

interface GiosgLivechatProps {
  isLivechatAvailable: () => boolean;
  messages: ChatMessage[];
  onMinimize: () => void;
  addMessageToHistory: (msg: ChatMessage) => void;
  sendMessageToBotBackend: (sender: string, content: string, options?: { autoPost?: boolean }) => void;
}

/**
 * Component that handles all Giosg (Apotek) livechat functionality.
 * This keeps ChatWidget clean by encapsulating all Giosg-specific logic.
 */
export const GiosgLivechat = ({ 
  isLivechatAvailable,
  messages,
  onMinimize,
  addMessageToHistory,
  sendMessageToBotBackend
}: GiosgLivechatProps) => {
  const { internalEventEmitter } = useChatContext();
  const messagesRef = useRef(messages);
  messagesRef.current = messages; // Always keep ref current
  
  const handleLivechatStart = () => {
    if (!isLivechatAvailable()) {
      console.warn('[GiosgLivechat] Livechat is not available when trying to start Giosg');
      
      sendMessageToBotBackend("user", "Livechat unavailable.", { autoPost: true });
      
      const systemMessage: SystemMessage = {
        sender: 'system',
        content: 'Livechatten är för närvarande inte tillgänglig. Försök igen senare.',
        type: 'system'
      };
      addMessageToHistory(systemMessage);
      return;
    }
    
    onMinimize();
    
    const chatHistory = messagesRef.current
      .map((msg: ChatMessage) => {
        const isAutoPost = (msg as any).autoPost;
        const actorLabel = isAutoPost ? `${msg.type} (auto-posted)` : msg.type;
        return `${actorLabel}: ${msg.content}`;
      })
      .join('\n');
    
    domEventService.emitEvent('chatbotStartLivechat', {
      chatHistory: chatHistory
    });
  };

  useEffect(() => {
    const handleChatBotRequest = (payload: ChatBotRequestPayload) => {
      const data = { livechatAvailable: isLivechatAvailable() };
      Object.assign(payload.requestBody.context, data);
    };

    const handleChatResponse = (payload: ChatResponsePayload) => {
      if (payload.data.handover === true) {
        console.log('[GiosgLivechat] Handover in chatResponse');
        handleLivechatStart();
      }
    };

    const handleStartLivechatRequested = () => {
      console.log('[GiosgLivechat] Received startLivechatRequested event');
      handleLivechatStart();
    };

    internalEventEmitter.on(CHAT_BOT_REQUEST, handleChatBotRequest);
    internalEventEmitter.on(CHAT_RESPONSE, handleChatResponse);
    internalEventEmitter.on(START_LIVECHAT_REQUESTED, handleStartLivechatRequested);

    return () => {
      internalEventEmitter.off(CHAT_BOT_REQUEST, handleChatBotRequest);
      internalEventEmitter.off(CHAT_RESPONSE, handleChatResponse);
      internalEventEmitter.off(START_LIVECHAT_REQUESTED, handleStartLivechatRequested);
    };
  }, []);

  return null;
};
